/** @prettier */
import React, { useMemo } from "react";
import {
    FormGroup,
    ControlLabel,
    HelpBlock,
    FormControl,
    Radio,
    Button,
} from "react-bootstrap";
import { AnswerChoice } from "./questionApi";
import { uniqueId } from "../util/uniqueId";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export const DEFAULT_ANSWER_OPTIONS = [
    { choice: "", correct: false },
    { choice: "", correct: false },
    { choice: "", correct: false },
    { choice: "", correct: false },
    { choice: "", correct: false },
];

type QuestionAnswersEditorProps = {
    answerChoices: AnswerChoice[];
    setAnswerChoices: (choices: AnswerChoice[]) => void;
    validationErrors: string;
};
/** A symbol used so we can stash the React key prop on our answer choice objects without risk of conflicts. */
// ... also because I've never used symbols and symbols are cool.
const answerKeySymbol = Symbol();

const useAnswersWithKeys = (answerChoices: AnswerChoice[]) => {
    // Ensure the answer objects have keys (does a bit of mutation of the input array)
    return useMemo(() => {
        return answerChoices.map((choice) => {
            const choiceWithKey = choice as AnswerChoice & {
                [answerKeySymbol]: string;
            };
            if (!choiceWithKey[answerKeySymbol]) {
                // Deliberately mutating the input array, but it's safe since it's a symbol we own.
                choiceWithKey[answerKeySymbol] = uniqueId("answer");
            }
            return choiceWithKey;
        });
    }, [answerChoices]);
};

// ======
//  EDIT
// ======
export const QuestionAnswersEditor = ({
    answerChoices,
    setAnswerChoices,
    validationErrors,
}: QuestionAnswersEditorProps) => {
    const answers = useAnswersWithKeys(answerChoices);

    const addAnswer = () => {
        setAnswerChoices(answerChoices.concat({ choice: "", correct: false }));
    };
    const updateAnswer = (index: number, newValue: Partial<AnswerChoice>) => {
        const valueArray = answers.map((value, valueIndex) => {
            return valueIndex === index
                ? Object.assign(value, newValue)
                : {
                      choice: value.choice,
                      [answerKeySymbol]: value[answerKeySymbol],
                      correct: newValue.correct ? false : value.correct,
                  };
        });
        setAnswerChoices(valueArray);
    };
    const removeAnswer = (index: number) => {
        setAnswerChoices(
            answers
                .slice(0, index)
                .concat(answers.slice(index + 1, answers.length)),
        );
    };
    return (
        <FormGroup className="question-answers" controlId="question-answers">
            <ControlLabel>Answer choices</ControlLabel>
            <div>
                Each question must have at least 5 answer choices. Please select
                the correct answer.
            </div>
            {answers.map((value, index) => {
                return (
                    <QuestionAnswer
                        key={value[answerKeySymbol]}
                        index={index}
                        choice={value.choice}
                        correct={value.correct}
                        updateAnswer={updateAnswer}
                        removeAnswer={removeAnswer}
                    />
                );
            })}
            <Button onClick={addAnswer}>Add answer choice</Button>
            <HelpBlock className="validation-error has-error">
                {validationErrors}
            </HelpBlock>
        </FormGroup>
    );
};

type QuestionAnswerProps = {
    index: number;
    choice: string;
    correct?: boolean;
    updateAnswer: (index: number, newValue: Partial<AnswerChoice>) => void;
    removeAnswer: (index: number) => void;
};
const QuestionAnswer = (props: QuestionAnswerProps) => {
    const { index, choice, correct } = props;

    let placeHolderText = "Answer choice " + (index + 1);
    return (
        <Radio
            className="answer-choice-radio"
            name="radioGroup"
            checked={!!correct}
            onChange={() => props.updateAnswer(index, { correct: true })}
        >
            <FormControl
                className="question-answer"
                type="text"
                value={choice}
                placeholder={placeHolderText}
                onChange={(e) =>
                    props.updateAnswer(index, {
                        choice: (e.target as any).value,
                    })
                }
            />
            <Button bsStyle="link" onClick={() => props.removeAnswer(index)}>
                <FontAwesomeIcon icon="minus-square" />
            </Button>
        </Radio>
    );
};

// =====
// View
// ====
type QuestionAnswersViewProps = {
    answerChoices: Array<AnswerChoice>;
};
export const QuestionAnswersView = ({
    answerChoices,
}: QuestionAnswersViewProps) => {
    const answers = useAnswersWithKeys(answerChoices);
    return (
        <div>
            <FormGroup
                className="question-answers"
                controlId="question-answers"
            >
                <ControlLabel>Answer choices</ControlLabel>
                {answers.map((value) => (
                    <div key={value[answerKeySymbol]}>
                        <span className="correct-choice-indicator">
                            {value.correct && <FontAwesomeIcon icon="check" />}
                        </span>
                        <span>{value.choice}</span>
                    </div>
                ))}
            </FormGroup>
        </div>
    );
};
